import { useQuery } from '@tanstack/react-query';
import fdbService, {
  FdbCounselingResponse,
  FdbWarningsResponse,
  FdbSideEffectsResponse,
  FdbFoodInteractionsResponse,
  FdbPrecautionsResponse,
  FdbPatientEducationResponse,
  FdbDurScreenResponse,
  FdbDrugInfo,
  FdbIndicationsResponse,
  FdbDosageResponse,
  FdbIngredientsResponse,
  FdbMonographBundle,
} from '../../services/fdbService';
import { rxService } from '../../services/rxService';

// FDB data is static clinical reference — cache aggressively
const FDB_STALE_TIME = 30 * 60 * 1000;

/**
 * Wraps an FDB service call to return null on 404 (DIN not in FDB)
 * instead of throwing, since many drugs won't be in FDB.
 */
async function withNotFoundHandling<T>(fn: () => Promise<T>): Promise<T | null> {
  try {
    return await fn();
  } catch (err: any) {
    if (err.response?.status === 404) return null;
    throw err;
  }
}

/**
 * Fetches all FDB clinical data for a drug by DIN, plus patient DUR screen
 * for drug-drug interactions across the patient's full medication profile.
 *
 * DUR screen works by:
 * 1. Fetching the patient's active prescriptions via rxService (same source as Patient 360 drug table)
 * 2. Extracting DINs from those prescriptions
 * 3. Calling POST /fdb/dur/screen with those DINs
 *
 * All queries fire in parallel. Each is independently cached.
 * Returns null data (not error) when DIN is not found in FDB.
 */
export function useFdbClinicalData(
  din: string | undefined,
  options?: {
    patientAge?: number;
    patientId?: string;
    pharmacyId?: string;
    patientGender?: string;
    enabled?: boolean;
  },
) {
  const { patientAge, patientId, pharmacyId, patientGender, enabled = true } = options ?? {};
  const queryEnabled = enabled && !!din;
  const rxEnabled = enabled && !!patientId && !!pharmacyId;

  // Dosage uses age-in-days; precautions uses age-in-years. Thread both into the bundle.
  const patientAgeDays = patientAge ? Math.round(patientAge * 365.25) : undefined;

  // Single request fetches all 9 monograph sections at once (was 9 separate calls).
  const bundle = useQuery<FdbMonographBundle | null>({
    queryKey: ['fdb', 'monograph-bundle', din, patientAge],
    queryFn: () => withNotFoundHandling(() =>
      fdbService.getMonographBundle(din!, { patientAge, patientAgeDays }),
    ),
    enabled: queryEnabled,
    staleTime: FDB_STALE_TIME,
  });

  // Re-expose each section as a query-like object so consumers keep reading
  // `.data` / `.isLoading` / `.isFetched` exactly as before.
  const section = <T>(value: T | null | undefined) => ({
    data: (bundle.data ? (value ?? null) : null) as T | null,
    isLoading: bundle.isLoading,
    isFetched: bundle.isFetched,
  });

  const counseling = section<FdbCounselingResponse>(bundle.data?.counseling);
  const warnings = section<FdbWarningsResponse>(bundle.data?.warnings);
  const sideEffects = section<FdbSideEffectsResponse>(bundle.data?.sideEffects);
  const foodInteractions = section<FdbFoodInteractionsResponse>(bundle.data?.foodInteractions);
  const precautions = section<FdbPrecautionsResponse>(bundle.data?.precautions);
  const patientEducation = section<FdbPatientEducationResponse>(bundle.data?.patientEducation);
  const indications = section<FdbIndicationsResponse>(bundle.data?.indications);
  const dosage = section<FdbDosageResponse>(bundle.data?.dosage);
  const ingredients = section<FdbIngredientsResponse>(bundle.data?.ingredients);

  // drugInfo is a distinct endpoint (not part of the monograph bundle) — keep separate.
  const drugInfo = useQuery<FdbDrugInfo | null>({
    queryKey: ['fdb', 'drug-info', din],
    queryFn: () => withNotFoundHandling(() => fdbService.getDrugInfo(din!)),
    enabled: queryEnabled,
    staleTime: FDB_STALE_TIME,
  });

  // Step 1: Fetch patient's active prescriptions to get all their DINs
  const patientRx = useQuery({
    queryKey: ['rx', 'prescriptions', pharmacyId, patientId, 'active'],
    queryFn: () => rxService.getRxList(pharmacyId!, patientId!, undefined, 'active'),
    enabled: rxEnabled,
    staleTime: FDB_STALE_TIME,
  });

  // Step 2: Extract unique DINs and build DIN→last fill date lookup
  const patientDins = patientRx.data
    ?.map((rx) => rx.drugDIN)
    .filter((d): d is string => !!d && d.length > 0)
    .filter((d, i, arr) => arr.indexOf(d) === i) // dedupe
    ?? [];

  // Map DIN → most recent fill date (for display in DDI section)
  const dinFillDates: Record<string, string> = {};
  if (patientRx.data) {
    for (const rx of patientRx.data) {
      if (!rx.drugDIN || !rx.fillDate) continue;
      const existing = dinFillDates[rx.drugDIN];
      if (!existing || rx.fillDate > existing) {
        dinFillDates[rx.drugDIN] = rx.fillDate;
      }
    }
  }

  const durScreen = useQuery<FdbDurScreenResponse | null>({
    queryKey: ['fdb', 'dur-screen', patientDins],
    queryFn: () => withNotFoundHandling(() =>
      fdbService.screenDur(patientDins, patientAge, patientGender),
    ),
    // Only run when we have 2+ DINs (need at least 2 for DDI checks)
    enabled: rxEnabled && patientDins.length >= 2,
    staleTime: FDB_STALE_TIME,
  });

  const isAnyLoading =
    counseling.isLoading || warnings.isLoading || sideEffects.isLoading ||
    foodInteractions.isLoading || precautions.isLoading || patientEducation.isLoading ||
    drugInfo.isLoading || indications.isLoading;

  const hasAnyData =
    !!counseling.data || !!warnings.data || !!sideEffects.data ||
    !!foodInteractions.data || !!precautions.data || !!patientEducation.data ||
    !!drugInfo.data || !!indications.data;

  // All queries finished but none returned data (all 404)
  const dinNotInFdb =
    !isAnyLoading && !hasAnyData &&
    counseling.isFetched && warnings.isFetched && sideEffects.isFetched &&
    foodInteractions.isFetched && precautions.isFetched && patientEducation.isFetched &&
    drugInfo.isFetched && indications.isFetched;

  return {
    counseling,
    warnings,
    sideEffects,
    foodInteractions,
    precautions,
    patientEducation,
    drugInfo,
    indications,
    dosage,
    ingredients,
    durScreen,
    patientDins,
    dinFillDates,
    isAnyLoading,
    hasAnyData,
    dinNotInFdb,
  };
}
